import React, { useState, useEffect } from 'react';
import { getLinkedAccounts, unlinkAccount, getPlaidStatus } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Building2,
  CreditCard,
  RefreshCw,
  Unlink,
  Clock,
  CheckCircle2,
  AlertCircle,
  Link2,
  Wallet,
  PiggyBank,
  Loader2
} from 'lucide-react';

const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const getAccountIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'credit':
      return <CreditCard className="w-5 h-5" />;
    case 'investment':
      return <PiggyBank className="w-5 h-5" />;
    default:
      return <Wallet className="w-5 h-5" />;
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export default function LinkedAccounts({ compact = false, showTitle = true }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [unlinking, setUnlinking] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await getLinkedAccounts();
      setAccounts(response.data.accounts || []);
      setConfigured(response.data.configured);
    } catch (error) {
      console.error('Error fetching linked accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (accountId, accountName) => {
    if (!window.confirm(`Are you sure you want to unlink ${accountName}?`)) return;
    
    setUnlinking(accountId);
    try {
      await unlinkAccount(accountId);
      setAccounts(prev => prev.filter(acc => acc.id !== accountId));
      toast.success(`${accountName} unlinked successfully`);
    } catch (error) {
      toast.error('Failed to unlink account');
    } finally {
      setUnlinking(null);
    }
  };

  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading accounts...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state when no accounts are linked
  if (accounts.length === 0) {
    return (
      <Card className="card-premium">
        {showTitle && (
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" />
              Linked Bank Accounts
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="text-center py-6 px-4">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
              <Link2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium mb-1">No accounts connected</p>
            <p className="text-sm text-muted-foreground mb-4">
              {configured 
                ? "Link your bank accounts to automatically import transactions"
                : "Bank linking is not configured yet"}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/business/expenses/manage'}
              className="border-[hsl(var(--accent))]/30 hover:bg-[hsl(var(--accent))]/10"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Link Bank Account
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact view for sidebar or small widgets
  if (compact) {
    return (
      <Card className="card-premium">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[hsl(var(--accent))]" />
              Linked Accounts ({accounts.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {accounts.slice(0, 3).map((account) => (
            <div 
              key={account.id}
              className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
            >
              <div className="flex items-center gap-2">
                <div className="text-[hsl(var(--accent))]">
                  {getAccountIcon(account.type)}
                </div>
                <div>
                  <p className="text-sm font-medium">{account.institution}</p>
                  <p className="text-xs text-muted-foreground">••••{account.mask}</p>
                </div>
              </div>
              <Badge 
                variant="outline" 
                className={account.status === 'active' 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                  : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                }
              >
                {account.status === 'active' ? 'Active' : 'Needs Attention'}
              </Badge>
            </div>
          ))}
          {accounts.length > 3 && (
            <p className="text-xs text-center text-muted-foreground pt-1">
              +{accounts.length - 3} more accounts
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view with all details
  return (
    <Card className="card-premium">
      {showTitle && (
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" />
              Linked Bank Accounts
            </span>
            <Badge variant="outline" className="bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30">
              {accounts.length} Connected
            </Badge>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4">
        {accounts.map((account) => (
          <div 
            key={account.id}
            className="p-4 rounded-lg border border-border bg-muted/20 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]">
                  {getAccountIcon(account.type)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{account.institution}</h4>
                    <Badge 
                      variant="outline" 
                      className={account.status === 'active' 
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                        : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                      }
                    >
                      {account.status === 'active' ? (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                      ) : (
                        <><AlertCircle className="w-3 h-3 mr-1" /> Needs Attention</>
                      )}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {account.name || account.official_name} • ••••{account.mask}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Synced {formatTimeAgo(account.last_sync)}
                    </span>
                    <span className="capitalize">{account.type} • {account.subtype}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {account.current_balance !== null && account.current_balance !== undefined && (
                  <div className="text-right mr-4">
                    <p className="text-sm text-muted-foreground">Balance</p>
                    <p className="font-semibold">{formatCurrency(account.current_balance)}</p>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlink(account.id, account.name || account.institution)}
                  disabled={unlinking === account.id}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  {unlinking === account.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
        
        <div className="pt-2 border-t border-border flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Transactions sync automatically every 6 hours
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.location.href = '/business/expenses/manage'}
            className="border-[hsl(var(--accent))]/30 hover:bg-[hsl(var(--accent))]/10"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Link Another Account
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
